import { useRef } from 'react';
import { patchUi, ui } from '../app/ui';
import { useApp, useSession, useUi, useWindow, useWindowId } from '../app/context';
import { MAX_WINDOWS } from '../core/geometry';
import { play } from '../motion/motion';

/**
 * The window's control cluster, top left. At rest: small soft dots, grey in
 * a window without focus. When the pointer or keyboard comes near, the dots
 * swell slightly and show what they do — close, new window, maximise.
 *
 * A little apart from them sits a fourth key of the same family: a neutral
 * rounded square that divides into four panes on approach — Window Layout,
 * the one spatial action beside the three foundational ones. Its surface
 * grows out of it.
 *
 * Close exists only while there is something to close: with one window the
 * cluster is New, Maximize and Layout, because the canvas keeps its last
 * window. A second window lets Close unfold in front of the others; going back
 * to one folds it away again (width and opacity, so the neighbours glide
 * rather than jump). While folded it is inert and out of the tab order.
 */

const Glyph = ({ d }: { d: string }) => (
  <svg className="wc-glyph" width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" focusable="false">
    <path d={d} />
  </svg>
);

const CLOSE = 'M1.6 1.6 6.4 6.4M6.4 1.6 1.6 6.4';
const NEW = 'M4 1.2v5.6M1.2 4h5.6';
const MAXIMIZE = 'M4.6 1.4h2v2M3.4 6.6h-2v-2M6.6 1.4 4.5 3.5M1.4 6.6l2.1-2.1';
const RESTORE = 'M6.6 3.4h-2v-2M1.4 4.6h2v2M4.6 3.4l2-2M3.4 4.6l-2 2';

export function WindowControls() {
  const app = useApp();
  const id = useWindowId();
  const zone = useWindow((w) => w.zone);
  const count = useSession((s) => s.windows.length);
  const layoutOpen = useUi((s) => s.layoutFor === id);
  const newRef = useRef<HTMLButtonElement>(null);
  const full = count >= MAX_WINDOWS;
  const last = count === 1;
  const maximized = zone === 'max';

  // Window Layout opens from its own key; right-clicking the zoom control offers it too, the way desktop windows do.
  const openLayouts = () => {
    if (!ui.get().compact) patchUi({ layoutFor: id, contextMenu: null });
  };

  /** A small, physical "no": the control shakes its head. */
  const decline = (el: HTMLElement | null) =>
    play(el, [{ transform: 'translateX(0)' }, { transform: 'translateX(-2.5px)' }, { transform: 'translateX(2px)' }, { transform: 'translateX(-1px)' }, { transform: 'translateX(0)' }], { duration: 320, easing: 'ease-out' });

  const newWindow = () => {
    if (!app.newWindow()) decline(newRef.current);
  };

  return (
    <div className="wc" role="group" aria-label="Window" data-count={count} onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="wc-btn"
        data-kind="close"
        data-folded={last || undefined}
        aria-label="Close window"
        aria-hidden={last || undefined}
        inert={last || undefined}
        tabIndex={last ? -1 : undefined}
        data-tip="Close Window"
        data-tip-side="bottom"
        onClick={() => !last && app.closeWindow(id)}
      >
        <span className="wc-dot">
          <Glyph d={CLOSE} />
        </span>
      </button>
      <button
        ref={newRef}
        type="button"
        className="wc-btn"
        data-kind="new"
        aria-label="New window"
        aria-disabled={full || undefined}
        data-tip={full ? `Up to ${MAX_WINDOWS} windows` : 'New Window'}
        data-tip-side="bottom"
        onClick={newWindow}
      >
        <span className="wc-dot">
          <Glyph d={NEW} />
        </span>
      </button>
      <button
        type="button"
        className="wc-btn"
        data-kind="zoom"
        aria-label={maximized ? 'Restore window size' : 'Maximize window'}
        aria-pressed={maximized}
        data-tip={maximized ? 'Restore' : 'Maximize'}
        data-tip-side="bottom"
        onClick={() => app.toggleMaximize(id)}
        onContextMenu={(e) => {
          e.preventDefault();
          openLayouts();
        }}
      >
        <span className="wc-dot">
          <Glyph d={maximized ? RESTORE : MAXIMIZE} />
        </span>
      </button>
      <button
        type="button"
        className="wc-btn"
        data-kind="layout"
        aria-label="Window layout"
        aria-haspopup="dialog"
        aria-expanded={layoutOpen}
        data-tip={layoutOpen ? undefined : 'Window Layout'}
        data-tip-side="bottom"
        onClick={() => (layoutOpen ? patchUi({ layoutFor: null }) : openLayouts())}
      >
        <span className="wc-dot" data-morph-source>
          <LayoutGlyph />
        </span>
      </button>
    </div>
  );
}

/** Four panes: the rounded square's gutters open when the pointer or keyboard comes near. */
const LayoutGlyph = () => (
  <svg className="wc-glyph wc-panes" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path d="M6 0v12M0 6h12" />
  </svg>
);
